/**
 * CalculatorWidgets - Renders calculator widgets dynamically based on configuration
 */

import React from 'react';
import { Box, Typography, IconButton, Tooltip, Sheet, Chip, Button } from '@mui/joy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useWidgetConfig } from '../../hooks/useWidgetConfig';
import { useFamilyCompatibilityData } from '../../hooks/useFamilyCompatibilityData';
import { useIsMobile } from '../../hooks/useIsMobile';
import WidgetContainer from './WidgetContainer';
import BasicDetails from './BasicDetails';
import PlanetDetails from './PlanetDetails';
import YogasWidget from './YogasWidget';
import FiveFoldFriendsWidget from './FiveFoldFriendsWidget';
import LifeAreasDashboard from '../LifeAreasDashboard';
import HouseScoresCompactView from '../HouseScoresCompactView';
import VimshottariDashaTimeline from '../VimshottariDashaTimeline';
import DashaFocusOverview from '../DashaFocusOverview';
import ShodashaVargaTable from '../ShodashaVargaTable';
import NorthIndianChartVedicUMD from '../NorthIndianChartVedicUMD';
import FamilyHouseRelationshipsWidget from './FamilyHouseRelationshipsWidget';
import FamilyCompatibilityWidget from './FamilyCompatibilityWidget';
import MarriageCompatibilityWidget from './MarriageCompatibilityWidget';
import PersonalMuhurtaAdvisor from './PersonalMuhurtaAdvisor';
import AudioGuide from '../AudioGuide';
import LifeEventTimelineAnalyzer from './LifeEventTimelineAnalyzer';
import { PlanetName } from '../../utils/astroEngine';

interface CalculatorWidgetsProps {
  // All the data needed by various widgets
  astroData: any;
  selectedCity: any;
  utcDateTime: string;
  localDateTime: string;
  chartName?: string;
  
  // House scores and strength
  d1HouseScores: Record<number, number> | null;
  bhavaBalaRupas: Record<number, number> | null;
  
  // Shadbala and Ishta/Kashta
  shadbalaFooter: Partial<Record<PlanetName, number>> | null;
  ishtaKashtaFooter: Partial<Record<PlanetName, { ishta: number; kashta: number }>> | null;
  blendedPlanetPct: Partial<Record<PlanetName, number>> | null;
  
  // House names
  houseNames: Record<number, string>;
  
  // Chart data
  combust: Record<string, boolean> | null;
  debil: Record<string, boolean> | null;
  exalt: Record<string, boolean> | null;
  conjunctDebil: Record<string, boolean> | null;
  conjunctExalt: Record<string, boolean> | null;
  funcColor: (planet: string) => string;
  
  // Aspects
  d1AspectEdges: Array<{ from: PlanetName; toHouse: number }>;
  
  // Varga data (for shodashavarga)
  shIn: { ascLonDeg: number; planetLongitudesDeg: Partial<Record<PlanetName, number>> } | null;
  vargaMap?: any; // Divisional charts computed by computeImplementedVargas
  
  // Yogas and special conditions
  sadeSati?: {
    isActive: boolean;
    currentPhase?: string;
    from?: string;
    to?: string;
  } | null;
  kaalSarpYoga?: {
    isPresent: boolean;
    type?: string;
    specificType?: string;
    specificEffects?: string;
    hasRajYogaCombination?: boolean;
    rahuHouse?: number;
    ketuHouse?: number;
    planetsBetween?: string[];
  } | null;
  
  // For yoga generation
  dashaContext?: any;
  functionalClassification?: any;
  combustionMap?: any;
  
  // Active yogas from FeedsList (includes doshas and time-activated yogas)
  activeYogasFromFeeds?: any[];
  
  // Widget tour callback
  onStartWidgetTour?: (widgetKey: string) => void;
  
  // User ID for family widget
  userId?: string;
  
  // Selected profile ID (for checking if viewing main profile)
  selectedProfileId?: string | null;
  
  // Navigation callback for family widget
  onNavigateToBirthDetails?: () => void;

  // Temporal scoring (new 4-metric system)
  dashaImpactByHouse?: Record<number, number>;
  transitImpactByHouse?: Record<number, number>;
  transitPlanetHouses?: Array<{ planet: string; house: number }>;
  planetStrengthPct?: Partial<Record<string, number>>;
  reportLifeAreaRemedies?: Record<string, Array<{ title: string; description?: string; planet: string; isImportant?: boolean }>>;
  frictionScoresByPlanet?: Record<string, number>;

  // Analyse My Current Time button
  onAnalyseCurrentTime?: () => void;
  isAdmin?: boolean;
  remainingAnalyses?: number;

  // Life Event Timeline Analyzer v2 props
  functionalNature?: Record<string, string>;
  houseInterpretations?: any[];
}

export default function CalculatorWidgets(props: CalculatorWidgetsProps) {
  const { visibleWidgets, isLoading } = useWidgetConfig('calculator');
  
  // Fetch family compatibility data with yoga generation capability
  const { 
    members: familyMembers, 
    familyYogas, 
    loading: familyDataLoading,
    generateMissingYogas,
    generatingYogas,
    yogaGenerationProgress
  } = useFamilyCompatibilityData(
    props.userId || null,
    props.selectedProfileId || null
  );
  
  // Detect mobile device
  const { isMobile } = useIsMobile();
  
  // Widgets to hide on mobile (already shown in calculator tab)
  const mobileHiddenWidgets = [
    'north_indian_chart_d1',
    'd1_house_scores',
    'life_areas_dashboard',
    'vimshottari_timeline'
  ];
  
  // Filter out hidden widgets on mobile
  const filteredWidgets = isMobile 
    ? visibleWidgets.filter(w => !mobileHiddenWidgets.includes(w.widget_key))
    : visibleWidgets;
  
  // Debug: Log visible widgets order
  React.useEffect(() => {
    if (filteredWidgets.length > 0) {
      console.log('[CalculatorWidgets] Rendering', filteredWidgets.length, 'visible widgets:');
      filteredWidgets.forEach((w, idx) => {
        console.log(`  Position ${idx + 1}: ${w.widget_key} (order: ${w.display_order})`);
      });
    }
  }, [filteredWidgets]);
  
  const {
    astroData,
    selectedCity,
    utcDateTime,
    localDateTime,
    chartName,
    d1HouseScores,
    bhavaBalaRupas,
    shadbalaFooter,
    ishtaKashtaFooter,
    blendedPlanetPct,
    houseNames,
    combust,
    debil,
    exalt,
    funcColor,
    d1AspectEdges,
    shIn,
    sadeSati,
    kaalSarpYoga,
    dashaContext,
    functionalClassification,
    combustionMap,
    onStartWidgetTour,
  } = props;

  if (isLoading) {
    return <Box sx={{ p: 2 }}><Typography>Loading widgets...</Typography></Box>;
  }

  // Helper to render each widget
  const renderWidget = (widgetKey: string) => {
    switch (widgetKey) {
      case 'north_indian_chart_d1':
        return astroData ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-chart"
            audioCue="widget-north-indian-chart"
            title="North Indian Chart (D1)"
            description="Rāśi chart showing planetary positions"
            helpText="This is your birth chart - a snapshot of where planets were at your birth moment. Each diamond represents a house (life area). Planets in houses influence those areas of life. Colors show if planets are friendly (green) or challenging (red) for your chart."
            onHelp={() => onStartWidgetTour?.('north-indian-chart')}
          >
            <Box
              sx={{
                width: { xs: '100%', sm: '15cm' },
                height: { xs: 'auto', sm: '10cm' },
                minHeight: { xs: '400px', sm: 0 },
                border: '1px solid var(--joy-palette-neutral-outlinedBorder)',
                borderRadius: 'md',
                bgcolor: 'background.surface',
                p: '12px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  position: 'relative',
                  flex: 1,
                  minHeight: 0,
                  overflow: 'visible',
                  '& svg:not(.ni-grid-overlay) text': { display: 'none !important' },
                }}
              >
                <NorthIndianChartVedicUMD
                  cusps={astroData.cusps}
                  placements={astroData.placements}
                  widthPx={528}
                  heightPx={328}
                  scalePolicy="fit"
                  ascLonDeg={astroData.asc.siderealLongitudeDeg}
                  retroMap={{
                    Sun: astroData.grahas.Sun.retrograde,
                    Moon: astroData.grahas.Moon.retrograde,
                    Mercury: astroData.grahas.Mercury.retrograde,
                    Venus: astroData.grahas.Venus.retrograde,
                    Mars: astroData.grahas.Mars.retrograde,
                    Jupiter: astroData.grahas.Jupiter.retrograde,
                    Saturn: astroData.grahas.Saturn.retrograde,
                  }}
                  combustMap={combust ?? undefined}
                  debilMap={debil ?? undefined}
                  exaltMap={exalt ?? undefined}
                  includeNodeSpecialAspects={true}
                  getPlanetColor={funcColor}
                />
              </Box>
            </Box>
          </WidgetContainer>
        ) : null;

      case 'd1_house_scores':
        return d1HouseScores && bhavaBalaRupas ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-house-scores"
            audioCue="widget-house-scores"
            title="D1 House Scores — 4-Metric Analysis"
            helpText="Each house is rated by four metrics: Bhava Bala (structural strength), Planet Impact (karmic delivery), Dasha Impact (current period activation), and Transit Impact (live planetary pressure). The donut ring shows the combined weighted score (65% natal + 35% temporal)."
          >
            <HouseScoresCompactView
              d1HouseScores={d1HouseScores}
              bhavaBalaRupas={bhavaBalaRupas}
              houseNames={houseNames}
              dashaContext={props.dashaContext ? {
                MD: props.dashaContext.MD,
                AD: props.dashaContext.AD,
                PD: props.dashaContext.PD,
                SD: props.dashaContext.SD,
              } : undefined}
              planetPlacements={astroData ? Object.fromEntries(
                Object.entries(astroData.placements).map(([planet, data]: [string, any]) => [
                  planet, { house: data.house }
                ])
              ) : undefined}
              ascendantSignIndex={astroData?.asc?.ascendantSignIndex}
              combustion={(props.combustionMap ? Object.fromEntries(
                Object.entries(props.combustionMap).map(([k, v]: [string, any]) => [k, { combust: !!v }])
              ) : (combust ? Object.fromEntries(
                Object.entries(combust).map(([k, v]) => [k, { combust: !!v }])
              ) : null)) as any}
              dashaImpactByHouse={props.dashaImpactByHouse}
              transitImpactByHouse={props.transitImpactByHouse}
              transitPlanetHouses={props.transitPlanetHouses}
              planetStrengthPct={props.planetStrengthPct}
            />

          </WidgetContainer>
        ) : null;

      case 'life_areas_dashboard':
        return d1HouseScores && astroData ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-life-areas"
            audioCue="widget-life-areas-dashboard"
            title="Life Areas Dashboard"
            description="Consolidated view of major life domains"
            helpText="Quick overview of your main life areas. Each area card shows a combined score from four metrics: Bhava Bala, Planet Impact, Dasha Impact, and Transit Impact. The 4 colour-coded bars let you see at a glance which metric is driving the area's score."
            collapsible
          >
            <LifeAreasDashboard
              houseScores={d1HouseScores}
              bhavaBala={bhavaBalaRupas}
              dashaImpactByHouse={props.dashaImpactByHouse}
              transitImpactByHouse={props.transitImpactByHouse}
              remedies={props.reportLifeAreaRemedies}
              frictionScoresByPlanet={props.frictionScoresByPlanet && Object.keys(props.frictionScoresByPlanet).length > 0 ? props.frictionScoresByPlanet : undefined}
              planetPlacements={astroData ? Object.fromEntries(Object.entries(astroData.placements).map(([p, d]: [string, any]) => [p, { house: d.house }])) : undefined}
              ascendantSignIndex={astroData?.asc?.ascendantSignIndex}
            />
          </WidgetContainer>
        ) : null;

      case 'vimshottari_timeline':
        return utcDateTime && selectedCity && astroData ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-timeline"
            audioCue="widget-vimshottari-timeline"
            title="Vimśottarī Timeline + Dasha Focus"
            description="Mahādaśā periods with house strength overview"
            helpText="Your life timeline divided into planetary periods (dashas). Each planet rules your life for several years, bringing its specific energy. The current active period shows what themes dominate your life right now. Click the Dasha tab for detailed predictions."
            collapsible
          >
            <VimshottariDashaTimeline
              isoDate={utcDateTime}
              latitude={selectedCity.lat}
              longitude={selectedCity.lon}
              ascSign={astroData.asc.ascendantSignIndex}
              externalHouseScores={d1HouseScores}
              blendedPct={blendedPlanetPct ?? undefined}
              onAnalyseCurrentTime={props.onAnalyseCurrentTime}
              isAdmin={props.isAdmin}
              remainingAnalyses={props.remainingAnalyses}
              audioGuide={<AudioGuide src="/audio/guide-vimshottari.mp3" label="Dasha Guide" variant="red" />}
            />
          </WidgetContainer>
        ) : null;

      case 'dasha_focus_overview':
        return utcDateTime && selectedCity && astroData ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-dasha-focus"
            audioCue="widget-dasha-focus"
            title="Dasha Focus Overview"
            description="Current planetary periods with house activation details"
            helpText="Compact view of MD/AD/PD/SD dasha periods showing which houses each dasha planet rules, occupies, and aspects. Includes current transit positions and planets transiting through the dasha planet's occupied house."
            collapsible
          >
            <DashaFocusOverview
              key={`dasha-focus-${utcDateTime}-v2`}
              isoDate={utcDateTime}
              latitude={selectedCity.lat}
              longitude={selectedCity.lon}
              ascSign={astroData.asc.ascendantSignIndex}
              externalHouseScores={d1HouseScores}
              externalAspects={d1AspectEdges}
              blendedPct={blendedPlanetPct ?? undefined}
              onAnalyseCurrentTime={props.onAnalyseCurrentTime}
              isAdmin={props.isAdmin}
              remainingAnalyses={props.remainingAnalyses}
            />
          </WidgetContainer>
        ) : null;

      case 'basic_details':
        return astroData ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-basic-details"
            title="Basic Details"
            description="Birth information and astrological fundamentals"
            helpText="Your core astrological data: birth time, location, rising sign (Ascendant), and Moon sign. These are the foundation of your chart. Ascendant = your personality & appearance, Moon = emotions & mind. Planetary positions show where each planet sits in your chart."
            collapsible
            defaultExpanded={false}
          >
            <BasicDetails
              name={chartName}
              birthDateTime={localDateTime}
              birthPlace={selectedCity?.label}
              latitude={selectedCity?.lat}
              longitude={selectedCity?.lon}
              timezone={selectedCity?.tz}
              utcDateTime={utcDateTime}
              ascendant={{
                signIndex: astroData.asc.ascendantSignIndex,
                signName: astroData.asc.ascendantSignName,
                longitudeDeg: astroData.asc.siderealLongitudeDeg,
                nakshatra: astroData.asc.nakshatraName,
                pada: astroData.asc.pada,
              }}
              moonSign={{
                signIndex: astroData.moon.signIndex,
                signName: astroData.moon.signName,
                longitudeDeg: astroData.moon.siderealLongitudeDeg,
                nakshatra: astroData.moon.nakshatraName,
                pada: astroData.moon.pada,
              }}
              grahaLongitudes={{
                Sun: astroData.grahas.Sun.lon,
                Moon: astroData.grahas.Moon.lon,
                Mercury: astroData.grahas.Mercury.lon,
                Venus: astroData.grahas.Venus.lon,
                Mars: astroData.grahas.Mars.lon,
                Jupiter: astroData.grahas.Jupiter.lon,
                Saturn: astroData.grahas.Saturn.lon,
                Rahu: astroData.nodes.Rahu,
                Ketu: astroData.nodes.Ketu,
              }}
              houses={astroData.houses}
              planetsInHouses={astroData.placements}
            />
          </WidgetContainer>
        ) : null;

      case 'planet_details':
        return astroData ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-planet-details"
            title="Planet Details"
            description="Detailed planetary information with dignity status"
            helpText="Complete breakdown of each planet's position. Shows which zodiac sign they're in, which house (life area) they occupy, and their nakshatra (lunar mansion). Degrees show exact position. Combust = too close to Sun (weakened), Exalted = very strong, Debilitated = challenged."
            collapsible
            defaultExpanded={true}
          >
            <PlanetDetails
              grahaLon={{
                Sun: { lon: astroData.grahas.Sun.lon, retro: astroData.grahas.Sun.retrograde },
                Moon: { lon: astroData.grahas.Moon.lon, retro: astroData.grahas.Moon.retrograde },
                Mars: { lon: astroData.grahas.Mars.lon, retro: astroData.grahas.Mars.retrograde },
                Mercury: { lon: astroData.grahas.Mercury.lon, retro: astroData.grahas.Mercury.retrograde },
                Jupiter: { lon: astroData.grahas.Jupiter.lon, retro: astroData.grahas.Jupiter.retrograde },
                Venus: { lon: astroData.grahas.Venus.lon, retro: astroData.grahas.Venus.retrograde },
                Saturn: { lon: astroData.grahas.Saturn.lon, retro: astroData.grahas.Saturn.retrograde },
                Rahu: { lon: astroData.nodes.Rahu, retro: false },
                Ketu: { lon: astroData.nodes.Ketu, retro: false },
              }}
              placements={astroData.placements}
              combustMap={combust ?? undefined}
              debilMap={debil ?? undefined}
              exaltMap={exalt ?? undefined}
              conjunctDebilMap={props.conjunctDebil ?? undefined}
              conjunctExaltMap={props.conjunctExalt ?? undefined}
            />
          </WidgetContainer>
        ) : null;

      case 'yogas_analysis':
        return astroData ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-yogas"
            audioCue="widget-yogas-analysis"
            title="Active Yogas & Doshas"
            description="Present and absent yogas including special doshas"
            helpText="Special planetary combinations (yogas) found in your chart. These are like astrological 'bonuses' or 'warnings' - specific planet arrangements that create notable life patterns. Raj Yogas = success combinations, Arishta Yogas = challenges. Each yoga has specific timing and effects."
            collapsible
            defaultExpanded={true}
          >
            <YogasWidget
              placements={astroData.placements}
              ascendantSign={astroData.asc.ascendantSignIndex}
              sadeSati={sadeSati}
              kaalSarpYoga={kaalSarpYoga}
              dashaContext={dashaContext}
              functionalClassification={functionalClassification}
              combustionMap={combustionMap}
              chartSnapshot={astroData}
              houseScores={d1HouseScores}
              activeYogasFromFeeds={props.activeYogasFromFeeds}
            />
          </WidgetContainer>
        ) : null;

      case 'shodashavarga_table':
        return shIn ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-vargas"
            title="Ṣoḍaśavarga Table"
            description="Sixteen divisional chart dignities"
            helpText="Advanced divisional charts - like zooming into specific life areas. D1 = main chart, D9 (Navamsa) = marriage/dharma, D10 = career, D30 = troubles. Each chart reveals hidden details about that life aspect. Used by advanced astrologers for fine-tuned predictions."
            collapsible
            defaultExpanded={false}
          >
            <ShodashaVargaTable
              ascLonDeg={shIn.ascLonDeg}
              planetLongitudesDeg={shIn.planetLongitudesDeg}
              showTotals
              shadbalaPct={shadbalaFooter || undefined}
              ishtaKashta={ishtaKashtaFooter || undefined}
              aspectsD1={d1AspectEdges}
              planetSignedPct={blendedPlanetPct ?? undefined}
            />
          </WidgetContainer>
        ) : null;

      // TODO: Implement remaining widgets (shadbala_table, ishta_kashta_table)
      case 'five_fold_friends':
        return astroData ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-five-fold-friends"
            title="Five-Fold Relationships"
            description="Pañca-vargīya Maitri showing natural, temporal, and combined friendship"
            helpText="Shows how planets relate to each other in your chart - are they friends or enemies? This affects how well they work together. Strong friendships = harmonious energy, enmities = conflicts. Analyzes permanent, temporary, and current relationships for accuracy."
          >
            <FiveFoldFriendsWidget placements={astroData.placements} />
          </WidgetContainer>
        ) : null;

      case 'family_house_relationships':
        return props.userId ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-family-houses"
            audioCue="widget-family-house-relationships"
            title="Family House Relationships"
            description="View family relationships mapped to 12 Vedic houses with life area analysis"
            helpText="Vedic astrology assigns specific family relationships to houses based on traditional significations. This widget shows which family members belong to each house and their life area scores (Health, Wealth, Career, Marriage, Children, Travel) when their birth charts are available."
            audioGuide={<AudioGuide src="/audio/guide-family-houses.mp3" label="Family Guide" variant="red" />}
          >
            <FamilyHouseRelationshipsWidget 
              userId={props.userId}
              selectedProfileId={props.selectedProfileId}
              onNavigateToBirthDetails={props.onNavigateToBirthDetails}
              d1HouseScores={d1HouseScores}
              bhavaBalaRupas={bhavaBalaRupas}
              astroData={astroData}
            />
          </WidgetContainer>
        ) : null;

      case 'family_compatibility_dashboard':
        return props.userId ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-family-compatibility"
            title="🌟 Family Compatibility Dashboard"
            description="Pattern detection, yogas, dasha overlaps, and family dynamics insights"
            helpText="Advanced family analysis showing: common yogas (shared strengths), complementary patterns (who supports who), overlapping dasha periods (best timing for family decisions), composite patterns (family identity traits), and current transit impacts. Uses chart data, yogas, and house scores to reveal family dynamics."
          >
            <FamilyCompatibilityWidget 
              members={familyMembers}
              familyYogas={familyYogas}
              currentTransits={undefined} // TODO: Add current transits calculation
              onGenerateMissingYogas={generateMissingYogas}
              generatingYogas={generatingYogas}
              yogaGenerationProgress={yogaGenerationProgress}
            />
          </WidgetContainer>
        ) : null;

      case 'marriage_compatibility':
        return props.userId ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-marriage-compatibility"
            audioCue="widget-marriage-compatibility"
            title="💕 Marriage Compatibility Analysis"
            description="Vedic marriage compatibility between two charts"
            helpText="Comprehensive compatibility analysis including Moon sign distance (emotional dynamics), Gana/Nakshatra/Yoni compatibility, Lagna relationship, 7th house analysis, and Venus/Jupiter capacity for love and sustaining marriage. Select two birth profiles to compare."
            audioGuide={<AudioGuide src="/audio/guide-marriage-compatibility.mp3" label="Marriage Guide" variant="red" />}
          >
            <MarriageCompatibilityWidget 
              userId={props.userId}
              onNavigateToBirthDetails={props.onNavigateToBirthDetails}
            />
          </WidgetContainer>
        ) : null;

      case 'personal_muhurta_advisor':
        return astroData ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-muhurta-advisor"
            audioCue="widget-muhurta-advisor"
            title="⏰ Personal Muhurta Advisor"
            description="Find auspicious time windows with natal chart + transit integration"
            helpText="Enhanced Muhurta analysis combining traditional electional astrology with your natal chart, current dashas, and transits. Blocks Rahu Kaal and Gandanta periods. Ideal for business launches, property purchases, surgeries, and especially childbirth planning (mother's chart crucial)."
            audioGuide={<AudioGuide src="/audio/guide-muhurta.mp3" label="Muhurta Guide" variant="red" />}
          >
            <PersonalMuhurtaAdvisor
              astroData={astroData}
              d1HouseScores={d1HouseScores}
              bhavaBalaRupas={bhavaBalaRupas}
              dashaContext={dashaContext}
              selectedCity={selectedCity}
            />
          </WidgetContainer>
        ) : null;

      case 'life_event_timeline_analyzer':
        return astroData ? (
          <WidgetContainer
            key={widgetKey}
            sectionId="section-life-event-timeline"
            audioCue="widget-life-event-timeline"
            title="📈 Life Event Timeline Analyzer"
            description="Predict when events are most likely to happen naturally (1-year probability forecast)"
            helpText="Analyzes your natal chart strengths, dasha periods, and transits to predict when specific life events (business success, property acquisition, marriage, etc.) are most likely to manifest. Shows weekly probability scores combining natal (40%), dasha (40%), and transit (20%) factors."
            audioGuide={<AudioGuide src="/audio/guide-life-event-timeline.mp3" label="Timeline Guide" variant="red" />}
          >
            <LifeEventTimelineAnalyzer
              astroData={astroData}
              d1HouseScores={d1HouseScores}
              bhavaBalaRupas={bhavaBalaRupas}
              dashaContext={dashaContext}
              utcDateTime={utcDateTime}
              vargaMap={props.vargaMap}
              activeYogas={props.activeYogasFromFeeds}
              combust={combust ?? undefined}
              debil={debil ?? undefined}
              exalt={exalt ?? undefined}
              functionalNature={props.functionalNature}
              houseInterpretations={props.houseInterpretations}
            />
          </WidgetContainer>
        ) : null;

      case 'shadbala_table':
      case 'ishta_kashta_table':
        return (
          <WidgetContainer
            key={widgetKey}
            title={visibleWidgets.find(w => w.widget_key === widgetKey)?.display_name || widgetKey}
            description="Coming soon"
          >
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              This widget will be implemented soon.
            </Typography>
          </WidgetContainer>
        );

      default:
        return null;
    }
  };

  return (
    <>
      <Box
        sx={{
          display: {
            xs: 'flex',
            sm: 'flex',
            md: 'block', // Use block for column layout on medium+ screens
          },
          flexDirection: 'column',
          gap: {
            xs: 2,
            sm: 2,
            md: 0, // No gap needed with columns
          },
          columnCount: {
            xs: 1,
            sm: 1,
            md: 2, // 2 columns on medium+ screens
          },
          columnGap: 2, // Horizontal space between columns
          columnRule: 'none', // Remove vertical divider line between columns
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
          pt: { xs: 2, sm: 2, md: 0 }, // Top padding on mobile to prevent sticky header overlap
        }}
      >
        {filteredWidgets.map(widget => (
          <React.Fragment key={widget.widget_key}>
            <Box
              sx={{
                breakInside: 'avoid', // Prevent widgets from breaking across columns
                marginBottom: 2, // Spacing between widgets
                paddingX: { xs: 0, sm: 0, md: 1 }, // Horizontal padding on medium+ screens
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {renderWidget(widget.widget_key)}
            </Box>
            {/* Analyse My Current Time — injected immediately after Vimshottari Timeline */}
            {widget.widget_key === 'vimshottari_timeline' && props.onAnalyseCurrentTime && props.utcDateTime && props.selectedCity && props.astroData && (
              <Box sx={{ breakInside: 'avoid', marginBottom: 2, paddingX: { xs: 0, sm: 0, md: 1 } }}>
                <Box sx={{
                  p: 2, borderRadius: 'lg',
                  background: 'linear-gradient(135deg, #5c6bc0 0%, #7b1fa2 100%)',
                  display: 'flex', flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1.5,
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography level="title-sm" sx={{ color: 'white', fontWeight: 700, mb: 0.25 }}>
                      ⚡ Analyse My Current Time
                    </Typography>
                    <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.82)' }}>
                      6-week forecast · Moon tracking · Life area scores · 3-month summary
                    </Typography>
                    {!props.isAdmin && props.remainingAnalyses !== undefined && (
                      <Typography level="body-xs" sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.25 }}>
                        {props.remainingAnalyses > 0
                          ? `${props.remainingAnalyses} analyse${props.remainingAnalyses === 1 ? '' : 's'} remaining this month`
                          : 'Monthly quota reached — upgrade for more'}
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="solid" size="sm"
                    onClick={props.onAnalyseCurrentTime}
                    sx={{
                      flexShrink: 0, bgcolor: 'white', color: '#5c6bc0',
                      fontWeight: 700, fontSize: '0.8rem', whiteSpace: 'nowrap',
                      minWidth: { xs: '100%', sm: 'auto' },
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.9)', transform: 'translateY(-1px)' },
                    }}
                  >
                    🔮 Analyse Now
                  </Button>
                </Box>
              </Box>
            )}
          </React.Fragment>
        ))}
      </Box>
    </>
  );
}
